import { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";

import SbuxCard from "../../../components/SbuxCard";
import SbuxDrawer from "../../../components/SbuxDrawer";
import AdminIvrTable from "./table/ivr/AdminIvrTable";
import MainHoursMaterTable from "./table/mainHours/MainHoursMaterTable";
import SbuxFallback from "../../../components/SbuxFallback";
import SbuxStackSnackbar from "../../../components/SbuxStackSnackbar";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getMainHours } from "../../../services/mainHours";
import { getFeatureFlags } from "../../../services/featureFlags";
import { getResetQueue } from "../../../services/queue";
import { Box, Link } from "@mui/material";
import { ErrorBoundary } from "react-error-boundary";
import { logMessage } from "../../../utils/amplifyLogger";
import { clientLog } from "../../../services/logging";
import {
  SNACK_BARS_SEVERITY_SUCCESS,
  SNACK_BARS_SEVERITY_ERROR,
  ADMIN_QUEUE_DELETED_SUCCESSFULLY,
  ADMIN_HOURS_SAVED_SUCCESSFULLY,
  ADMIN_HOURS_ERROR_MESSAGE,
} from "../../../constants";
import QueueTabs from "./table/queue/QueueTable";
import FeatureFlagsTable from "../manager/table/featureFlags/FeatureFlagsTable";
import PortPhoneNumber from "./PortPhoneNumber";

import { getIvrTableStatusSelector } from "../../../selectors/ivrTableSelector";
import {
  getMainHoursDataSelector,
  getMainHoursIsSaveSelector,
  getMainHoursIsErrorSelector,
  getMainHoursStatusSelector,
} from "../../../selectors/mainHoursSelector";
import { selectFeatureFlags } from "../../../selectors/featureFlagSelector";
import {
  getQueueDeleteStatusSelector,
  getQueueDeleteErrorSelector,
} from "../../../selectors/queueSelector";
import SbuxTranslate from "../../../components/SbuxTranslate";
import { useAppDispatch, useAppSelector } from "../../../store/types";
import type { ReactNode } from "react";

interface FeatureFlag {
  doesFeatureFlagBelongToAdminControls?: boolean;
  [key: string]: unknown;
}

interface StatusReport {
  message: ReactNode;
  severity: string;
}

const Admin = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const status = useAppSelector(getIvrTableStatusSelector);
  const mainHoursData = useAppSelector(getMainHoursDataSelector) as Record<
    string,
    unknown
  >[];
  const isSave = useAppSelector(getMainHoursIsSaveSelector);
  const isError = useAppSelector(getMainHoursIsErrorSelector);
  const mainHoursStatus = useAppSelector(getMainHoursStatusSelector);
  const featureFlags = useAppSelector(selectFeatureFlags) as
    | FeatureFlag[]
    | null;
  const queueDeleteStatus = useAppSelector(getQueueDeleteStatusSelector);
  const queueDeleteError = useAppSelector(getQueueDeleteErrorSelector);

  const [showIvrIndicator, setShowIvrIndicator] = useState(false);
  const [showMainHoursIndicator, setShowMainHoursIndicator] = useState(false);

  const [showFeatureFlagIndicator, setShowFeatureFlagIndicator] =
    useState(false);

  const [showQueueIndicator, setShowQueueIndicator] = useState(false);
  const [showPortPhoneNumberComponent, setShowPortPhoneNumberComponent] =
    useState(false);

  const [featureFlagsData, setFeatureFlagsData] = useState<FeatureFlag[]>([]);

  const [openMainHoursSnackBar, setOpenMainHoursSnackBar] = useState(false);
  const [openQueueSnackBar, setOpenQueueSnackBar] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarReport, setSnackbarReport] = useState<StatusReport | null>(
    null,
  );

  useEffect(() => {
    setOpenMainHoursSnackBar(false);
    setOpenQueueSnackBar(false);
    setShowSnackbar(false);
    setSnackbarReport(null);
    dispatch(getResetQueue());
  }, []);

  useEffect(() => {
    (queueDeleteStatus === "deleted" || queueDeleteStatus === "rejected") &&
      setOpenQueueSnackBar((prevState) => !prevState);
  }, [queueDeleteStatus]);

  useEffect(() => {
    if (mainHoursStatus === "success" || mainHoursStatus === "save:rejected") {
      setOpenMainHoursSnackBar(false);
      setTimeout(() => setOpenMainHoursSnackBar(true), 0);
    }
  }, [mainHoursStatus]);

  useEffect(() => {
    if (featureFlags && featureFlags.length) {
      setFeatureFlagsData(
        featureFlags.filter((ff) => !!ff?.doesFeatureFlagBelongToAdminControls),
      );
    }
  }, [featureFlags]);

  useEffect(() => {
    return () => {
      setOpenMainHoursSnackBar(false);
      setOpenQueueSnackBar(false);
      setShowSnackbar(false);
      setSnackbarReport(null);
      setShowIvrIndicator(false);
      setShowMainHoursIndicator(false);
      setShowFeatureFlagIndicator(false);
      setShowQueueIndicator(false);
      setShowPortPhoneNumberComponent(false);
      dispatch(getResetQueue());
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setOpenMainHoursSnackBar(false);
        setOpenQueueSnackBar(false);
        setShowSnackbar(false);
        setSnackbarReport(null);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleError = (error: Error) => {
    clientLog({
      component: `Admin`,
      message: `Something went wrong in Admin Component => ${error}`,
    });
    logMessage(
      `Unhandle Error in Admin`,
      `Something went wrong in Admin Component => ${error}`,
      `error`,
    );
  };

  const handleIvrTableClick = () => {
    setShowIvrIndicator((prevInd) => !prevInd);
    setShowFeatureFlagIndicator(false);
    setShowMainHoursIndicator(false);
    setShowQueueIndicator(false);
    setShowPortPhoneNumberComponent(false);
    setShowSnackbar(false);
    setSnackbarReport(null);
  };
  const handleMainHoursTableClick = () => {
    setShowIvrIndicator(false);
    setShowFeatureFlagIndicator(false);
    setShowQueueIndicator(false);
    setShowMainHoursIndicator((prevInd) => {
      const next = !prevInd;
      if (!prevInd) dispatch(getMainHours());
      return next;
    });
    setShowPortPhoneNumberComponent(false);
    setShowSnackbar(false);
    setSnackbarReport(null);
  };

  const handleFeatureFlagTableClick = () => {
    setShowIvrIndicator(false);
    setShowMainHoursIndicator(false);
    setShowQueueIndicator(false);
    setShowFeatureFlagIndicator((prevInd) => {
      const next = !prevInd;
      if (!prevInd) dispatch(getFeatureFlags());
      return next;
    });
    setShowPortPhoneNumberComponent(false);
    setShowSnackbar(false);
    setSnackbarReport(null);
  };

  const handleQueueTableClick = async () => {
    setShowIvrIndicator(false);
    setShowMainHoursIndicator(false);
    setShowFeatureFlagIndicator(false);
    setShowQueueIndicator((prevInd) => !prevInd);
    setShowPortPhoneNumberComponent(false);
    setShowSnackbar(false);
    setSnackbarReport(null);
  };

  const handlePortPhoneNumberClick = () => {
    setShowIvrIndicator(false);
    setShowMainHoursIndicator(false);
    setShowQueueIndicator(false);
    setShowFeatureFlagIndicator(false);
    setShowPortPhoneNumberComponent(true);
    setShowSnackbar(false);
    setSnackbarReport(null);
  };

  const handleRefrehClick = async () => {
    showMainHoursIndicator && (await dispatch(getMainHours()));
  };

  const panelData = [
    {
      id: 100,
      name: <SbuxTranslate> {`Business Controls`}</SbuxTranslate>,
      panels: [
        {
          id: 101,
          url: (
            <Box sx={{ display: "flex" }}>
              <Link
                component="button"
                onClick={handleMainHoursTableClick}
                sx={{
                  outline: "none !important",
                  textDecoration: "none",
                }}
              >
                <span
                  style={{
                    color: showMainHoursIndicator
                      ? theme.palette.black[900]
                      : theme.palette.white[700],
                    paddingTop: 1,
                  }}
                >
                  <SbuxTranslate>{`Main Hours`}</SbuxTranslate>
                </span>
              </Link>
              {showMainHoursIndicator && (
                <ChevronRightIcon sx={{ fontSize: 21, paddingTop: 0.3 }} />
              )}
            </Box>
          ),
        },
        {
          id: 2,
          url: (
            <Box sx={{ display: "flex" }}>
              <Link
                component="button"
                onClick={handleFeatureFlagTableClick}
                sx={{
                  outline: "none !important",
                  textDecoration: "none",
                }}
              >
                <span
                  style={{
                    color: showFeatureFlagIndicator
                      ? theme.palette.black[900]
                      : theme.palette.white[700],
                    paddingTop: 1,
                  }}
                >
                  <SbuxTranslate>{`Feature Flags`}</SbuxTranslate>
                </span>
              </Link>
              {showFeatureFlagIndicator && (
                <ChevronRightIcon sx={{ fontSize: 21, paddingTop: 0.3 }} />
              )}
            </Box>
          ),
        },
        {
          id: 3,
          url: (
            <Box sx={{ display: "flex" }}>
              <Link
                component="button"
                onClick={handlePortPhoneNumberClick}
                sx={{
                  outline: "none !important",
                  textDecoration: "none",
                }}
              >
                <span
                  style={{
                    color: showPortPhoneNumberComponent
                      ? theme.palette.black[900]
                      : theme.palette.white[700],
                    paddingTop: 1,
                  }}
                >
                  {" "}
                  <SbuxTranslate> {`Port Phone Number`}</SbuxTranslate>{" "}
                </span>
              </Link>
              {showPortPhoneNumberComponent && (
                <ChevronRightIcon sx={{ fontSize: 21, paddingTop: 0.3 }} />
              )}
            </Box>
          ),
        },
      ],
    },
    {
      id: 2,
      name: <SbuxTranslate> {`IVR Controls`}</SbuxTranslate>,
      panels: [
        {
          id: 1,
          url: (
            <Box sx={{ display: "flex" }}>
              <Link
                component="button"
                onClick={handleIvrTableClick}
                sx={{
                  outline: "none !important",
                  textDecoration: "none",
                }}
              >
                <span
                  style={{
                    color: showIvrIndicator
                      ? theme.palette.black[900]
                      : theme.palette.white[700],
                    paddingTop: 1,
                  }}
                >
                  <SbuxTranslate> {`Create Table`}</SbuxTranslate>
                </span>
              </Link>
              {showIvrIndicator && (
                <ChevronRightIcon sx={{ fontSize: 21, paddingTop: 0.2 }} />
              )}
            </Box>
          ),
        },
        {
          id: 2,
          url: (
            <Box sx={{ display: "flex" }}>
              <Link
                component="button"
                onClick={handleQueueTableClick}
                sx={{
                  outline: "none !important",
                  textDecoration: "none",
                }}
              >
                <span
                  style={{
                    color: showQueueIndicator
                      ? theme.palette.black[900]
                      : theme.palette.white[700],
                    paddingTop: 1,
                  }}
                >
                  <SbuxTranslate> {`Queue`}</SbuxTranslate>
                </span>
              </Link>
              {showQueueIndicator && (
                <ChevronRightIcon sx={{ fontSize: 21, paddingTop: 0.2 }} />
              )}
            </Box>
          ),
        },
      ],
    },
  ];

  const renderTable = () => {
    if (showIvrIndicator) {
      return <AdminIvrTable />;
    } else if (showMainHoursIndicator) {
      return (
        <MainHoursMaterTable
          mainHoursData={mainHoursData}
          handleRefrehClick={handleRefrehClick}
          isManager={false}
          disableAddNew={false}
          isLoading={mainHoursStatus === "pending"}
        />
      );
    } else if (showFeatureFlagIndicator) {
      return (
        <FeatureFlagsTable
          featureFlags={
            featureFlagsData as {
              name: string;
              enabled: boolean;
              [key: string]: unknown;
            }[]
          }
          handleRefrehClick={handleRefrehClick}
        />
      );
    } else if (showQueueIndicator) {
      return <QueueTabs />;
    } else if (showPortPhoneNumberComponent) {
      return (
        <PortPhoneNumber onProcessStatusChange={handleProcessStatusChange} />
      );
    }

    return null;
  };

  const handleProcessStatusChange = (report: StatusReport) => {
    setSnackbarReport(report);
    setShowSnackbar(true);
  };

  const handleOpenHoursClose = (_event: unknown, reason?: string) => {
    if (reason === "clickaway") return;
    setOpenMainHoursSnackBar(false);
  };

  const handleQueueClose = (_event: unknown, reason?: string) => {
    if (reason === "clickaway") return;
    setOpenQueueSnackBar(false);
  };

  const handleMainHoursMessage = () => {
    return (
      <SbuxStackSnackbar
        open={openMainHoursSnackBar}
        message={
          isError ? (
            <SbuxTranslate>{ADMIN_HOURS_ERROR_MESSAGE}</SbuxTranslate>
          ) : (
            <SbuxTranslate>{ADMIN_HOURS_SAVED_SUCCESSFULLY}</SbuxTranslate>
          )
        }
        severity={
          isError ? SNACK_BARS_SEVERITY_ERROR : SNACK_BARS_SEVERITY_SUCCESS
        }
        handleClose={handleOpenHoursClose}
      />
    );
  };

  const handleQueueMessage = () => {
    return (
      <SbuxStackSnackbar
        open={openQueueSnackBar}
        message={
          queueDeleteStatus === "deleted" ? (
            <SbuxTranslate>{ADMIN_QUEUE_DELETED_SUCCESSFULLY}</SbuxTranslate>
          ) : (
            queueDeleteStatus === "rejected" && (
              <SbuxTranslate>{queueDeleteError}</SbuxTranslate>
            )
          )
        }
        severity={
          queueDeleteStatus === "rejected"
            ? SNACK_BARS_SEVERITY_ERROR
            : SNACK_BARS_SEVERITY_SUCCESS
        }
        handleClose={handleQueueClose}
      />
    );
  };

  return (
    <>
      {(isSave || isError) && handleMainHoursMessage()}
      {(queueDeleteStatus === "deleted" || queueDeleteStatus === "rejected") &&
        handleQueueMessage()}

      {showSnackbar && (
        <SbuxStackSnackbar
          open={showSnackbar}
          message={<SbuxTranslate>{snackbarReport?.message}</SbuxTranslate>}
          severity={snackbarReport?.severity}
          handleClose={() => setShowSnackbar(false)}
        />
      )}

      <ErrorBoundary
        FallbackComponent={SbuxFallback}
        onError={handleError}
        onReset={handleRefrehClick}
      >
        <SbuxCard className="insideTabContainer">
          {status === "rejected" ? (
            <SbuxFallback />
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <SbuxDrawer panelData={panelData}>{renderTable()}</SbuxDrawer>
              </Box>
            </>
          )}
        </SbuxCard>
      </ErrorBoundary>
    </>
  );
};

export default Admin;
